import { Container } from "reactstrap";
import styles from "./styles.module.scss";
import cn from "classnames";

// Decide whether a hex colour is light (needs dark text) or dark (needs light text)
const isLightColor = (hex) => {
  if (!hex || typeof hex !== "string") return false;
  const c = hex.replace("#", "");
  if (c.length !== 6) return false;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6;
};

// Shade a hex colour by a percentage (negative = darker, positive = lighter)
const shadeHex = (hex, percent) => {
  if (!hex || typeof hex !== "string") return hex;
  const c = hex.replace("#", "");
  if (c.length !== 6) return hex;
  const t = percent < 0 ? 0 : 255;
  const p = Math.abs(percent) / 100;
  const mix = (channel) => {
    const v = Math.round((t - channel) * p + channel);
    return v.toString(16).padStart(2, "0");
  };
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return `#${mix(r)}${mix(g)}${mix(b)}`;
};

const BsArrowRightCircleFill = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="1em"
    height="1em"
    fill="currentColor"
    viewBox="0 0 16 16"
    {...props}
  >
    <path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0M4.5 7.5a.5.5 0 0 0 0 1h5.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5z" />
  </svg>
);

export const FeatureSplit = ({
  className,
  title,
  heading,
  content,
  items,
  reverse,
  background,
  buttonLink,
  buttonTitle,
  buttonNewTab,
}) => {
  return (
    <section
      className={cn(
        styles.FeatureSplit,
        className,
        reverse && styles.reverse,
        styles[background]
      )}
    >
      <Container className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.content}>
            {title && <h2 data-sourceflow-prop="title" className={`${heading}`}>{title}</h2>}
            {content && (
              <div
                data-sourceflow-prop="content"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            )}
            {buttonLink && buttonTitle && (
              <div className="mt-4">
                <a
                  href={buttonLink}
                  target={buttonNewTab ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="primaryBtn no-arrow m-0"
                  area-label={`${buttonTitle} - ${buttonLink.replace(/^\/+/, '').replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}`}
                >
                  {buttonTitle}
                </a>
              </div>
            )}
          </div>
          <div className={styles.itemList}>
            {items?.map((item, i) => {
              // Fall back to the legacy variant values, then to Primary.
              const boxColor =
                item?.boxColor ||
                (item?.variant === "yellowWhite" ? "#FFC300" : "#14375A");
              const light = isLightColor(boxColor);
              // Hover colour: explicit choice, otherwise a clearly visible shift of the
              // box colour — dark boxes lighten, light boxes darken.
              const hoverColor =
                item?.boxHoverColor ||
                (light ? shadeHex(boxColor, -20) : shadeHex(boxColor, 24));
              const hoverLight = isLightColor(hoverColor);
              return (
              <a
                href={item?.url || "#"}
                className={cn(styles.item, light ? styles.isLight : styles.isDark)}
                style={{
                  "--box-color": boxColor,
                  "--box-color-hover": hoverColor,
                  "--box-fg-hover": hoverLight ? "#000" : "#fff",
                  "--box-fg-hover-muted": hoverLight
                    ? "rgba(0, 0, 0, 0.7)"
                    : "rgba(255, 255, 255, 0.75)",
                }}
                key={i}
                target={item?.linkNewTab ? '_blank' : '_self'}
                rel="noopener noreferrer"
              >
                {item?.icon?.url && <img src={item.icon.url} alt="" />}
                <div className={styles.texts}>
                  <h4>{item.title}</h4>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: item.description,
                    }}
                  />
                </div>
                <BsArrowRightCircleFill />
              </a>
              );
            })}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default FeatureSplit;
